/**
 * LeaderboardPage
 *
 * The global community leaderboard from GET /gamification/leaderboard. Only
 * users who opted in (public profile that shows authority scores) are listed;
 * the server enforces it. Highlights the current user's position.
 */

import { useAuth } from '../hooks/useAuth';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { LeaderboardTable } from '../components/leaderboard/LeaderboardTable';
import { LeaderboardOptInNotice } from '../components/leaderboard/LeaderboardOptInNotice';

export function LeaderboardPage() {
  const { user } = useAuth();

  const { entries, currentUserRank, currentUserStats, isOptedIn, isLoading, error } = useLeaderboard();

  const currentUserEntry = currentUserRank !== null
    ? entries.find((e) => e.userId === user?.id) ?? null
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leaderboard</h1>
          <p className="mt-1 text-gray-500">
            See how you stack up against the community
          </p>
        </div>

        {/* Current user rank badge */}
        {currentUserEntry && (
          <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">
                #{currentUserEntry.rank}
              </div>
              <div className="text-xs text-primary-700">Your Rank</div>
            </div>
            <div className="w-px h-10 bg-primary-200" />
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">
                {currentUserEntry.authorityScore}
              </div>
              <div className="text-xs text-primary-700">Avg Score</div>
            </div>
          </div>
        )}
      </div>

      {!isLoading && (
        <LeaderboardOptInNotice isOptedIn={isOptedIn} currentUserRank={currentUserRank} />
      )}

      {/* Error state */}
      {error && (
        <div className="card">
          <div className="card-body text-center py-8">
            <div className="text-red-500 mb-2">
              <ExclamationIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-600">Failed to load leaderboard data</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Leaderboard Table */}
      {!error && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Global Rankings</h2>
            <span className="text-sm text-gray-500">
              {entries.length} {entries.length === 1 ? 'user' : 'users'}
            </span>
          </div>
          <div className="card-body p-0">
            <LeaderboardTable
              entries={entries}
              currentUserId={user?.id}
              isLoading={isLoading}
              showDomain={false}
              showBadges={false}
              showHireStatus={false}
            />
          </div>
        </div>
      )}

      {/* Your stats card (if not on the board) */}
      {currentUserStats && !currentUserEntry && (
        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-primary-900">Your Stats</h3>
                <p className="text-sm text-primary-700">
                  Keep practicing to climb the leaderboard!
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-primary-600">
                    {currentUserStats.avgScore ?? '—'}
                  </div>
                  <div className="text-xs text-primary-700">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-primary-600">
                    {currentUserStats.sessionsCount}
                  </div>
                  <div className="text-xs text-primary-700">Sessions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips card */}
      <div className="card">
        <div className="card-body">
          <h3 className="font-semibold text-gray-900 mb-3">How to Climb the Ranks</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              Complete assessments to increase your authority score
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              Practice regularly to improve your scores
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              Master multiple domains to stand out
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              Earn badges by achieving milestones
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Icon components
function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
